import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { isEmpty } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { Col, Row } from "react-bootstrap";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import { Button } from "primereact/button";
import GroupedLoadsByRoute from "./GroupedLoadsByRoute";

interface IFormProps {
    id: string | null; // groupToken
    t: any | null;
    onRefresh?: any | null;
    dropdowns?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

type TInputForm = {
    comments?: any;
};

const DEFAULT_FORM_STATE: TInputForm = {
    comments: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        comments: yup.string().nullable(),
    });
};

function AlpOutboundLoadGroupReset(props: IFormProps) {
    const { toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);

    const onClose = () => {
        setPanelState(false);
        if (props.onClose) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/reset`,
                data: payloads,
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            resetForm();
            onClose();

            if (props.onRefresh) {
                props.onRefresh();
            }
        } catch (e: any) {
            if (e.status === 422) {
                setErrors(e.data.errors);
            } else if (e.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    const getDetail = async (groupToken: string) => {
        try {
            const response = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const data = response?.data?.data ?? response?.data;
            setEntity(data);
            setInitFormState({ ...DEFAULT_FORM_STATE });
            setPanelState(true);
        } catch (error: any) {
            if (error.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    useEffect(() => {
        setEntity(null);
        if (!isEmpty(props.id)) {
            // @ts-ignore
            getDetail(props.id);
        }
    }, [props.id]);

    const loads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t("reset load") }</h5>
                            <p className="mb-0">
                                <strong>{ t("group token") }:</strong> { props.id }
                            </p>
                        </div>
                    }
                    visible={ panelState }
                    position="right"
                    onHide={ onClose }
                    blockScroll={ true }
                    style={ { width: '800px' } }
                    closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (fp: FormikProps<TInputForm>) => (
                            <Form onSubmit={ fp.handleSubmit }>
                                <Row className={ "mt-4 mb-7" }>
                                    <Col sm={ 12 } md={ 12 }>
                                        <Field
                                            component={ InputTextAreaField }
                                            name="comments"
                                            label={ t('comments') }
                                            placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                        />
                                    </Col>

                                    <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                        <hr />
                                        <h4 className="card-title">{ t('loads') }</h4>
                                        <GroupedLoadsByRoute loads={ loads } t={ t } />
                                    </Col>
                                </Row>

                                <div className="filter-card-footer" style={ { width: '785px' } }>
                                    <Button
                                        tooltip={ t("to data save into database") }
                                        size={ "small" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-save me-2" />
                                        { t("let's save") }
                                    </Button>
                                    <Button
                                        type={ "button" }
                                        tooltip={ t("operation cancel") }
                                        className={ "ms-2" }
                                        size={ "small" }
                                        severity={ "danger" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        onClick={ onClose }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-arrow-left me-2" />
                                        { t("cancel") }
                                    </Button>
                                </div>
                            </Form>
                        ) }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

AlpOutboundLoadGroupReset.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupReset);

